import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { managedListen } from '@heidi/plugin-core';
import { type Region } from '@heidi/schema/ml-scribe';

/**
 * Carries the one authoritative prefetch state. One event rather than one per
 * outcome: two channels would let a readiness and a decline be observed in
 * either order.
 */
export const UPDATER_PREFETCH_STATE_EVENT = 'updater://prefetch-state';

export type PrefetchState = 'idle' | 'downloading' | 'ready' | 'failed';

/** Mirrored field for field; the renderer derives none of it. */
export type UpdaterPrefetchStatePayload = {
  state: PrefetchState;
  /** The version the state describes. Absent only on `idle`. */
  version?: string;
  /**
   * The feed the state was established on. Absent for the global feed, which
   * binds no region, and on `idle`.
   */
  region?: Region;
  /** Why a `failed` state failed. */
  reason?: string;
  /**
   * Whether a `failed` state spent the retry budget its cause was entitled to.
   * Always on the wire: only a transient cause has a budget, so a recurring
   * cause and every other state report `false` rather than omitting it.
   */
  retriesExhausted?: boolean;
  /** How long the download that produced the ready bytes took. */
  prefetchMs?: number;
};

export type UpdaterPrefetchStateHandler = (payload: UpdaterPrefetchStatePayload) => void;

let registration: Promise<void> | undefined;
// The registered listener reads through this, so a later call replaces the
// handler instead of being ignored by the memo below. Replacement is safe: the
// handler holds no per-request state and only writes what the payload says.
let liveHandler: UpdaterPrefetchStateHandler | undefined;

/**
 * Subscribe to the prefetch state, once per renderer; memoised, so a second
 * call cannot double every transition. Must be awaited BEFORE an offer is
 * recorded, or the warm-cache readiness is published with nothing listening.
 */
export function listenToUpdaterPrefetchState(
  handler: UpdaterPrefetchStateHandler
): Promise<void> {
  if (!isTauri()) return Promise.resolve();
  liveHandler = handler;
  registration ??= managedListen<UpdaterPrefetchStatePayload>(
    UPDATER_PREFETCH_STATE_EVENT,
    (event) => liveHandler?.(event.payload)
  )
    .then(() => undefined)
    .catch((error: unknown) => {
      // Retryable: clearing the memo lets the next check try again rather
      // than leaving the prompt permanently gated on an event nobody hears.
      registration = undefined;
      logger.warn('[updater] could not subscribe to the prefetch state', {
        tag: 'updater_prefetch_listen_failed',
        errorName: error instanceof Error ? error.name : typeof error,
      });
      throw error;
    });
  return registration;
}

/** Test seam: drop the memoised registration between cases. */
export function __resetUpdaterPrefetchListenersForTests(): void {
  registration = undefined;
  liveHandler = undefined;
}
